import { z } from 'zod';
import { UsdAmount } from './ai.js';
/**
 * Căn chỉnh định dạng `.chilldoc` — APM-CD-g/-h, docs/plan-chilldoc.md §3.7.
 *
 * AI (skill `format_document`) chỉ đề xuất **kế hoạch định dạng**, không viết
 * lại chữ. Client áp kế hoạch tất định (`applyFormatPlan`), cho người dùng xem
 * trước/sau rồi mới áp — luật #6. Đường làm tay dùng `DEFAULT_FORMAT_PLAN` qua
 * đúng hàm áp ấy.
 *
 * Đợt hai (APM-FMT-a, 2026-09-29): kế hoạch biết thêm thụt lề theo lớp, tiêu đề
 * đánh số, ảnh và công thức. Mọi trường mới **tuỳ chọn** — kế hoạch đợt một
 * parse y nguyên và áp y nguyên. Không có `codeFont`: mark `code` của TipTap
 * loại trừ mọi mark khác và `codeBlock` không nhận mark, nên font của mã không
 * đặt được qua `textStyle`; "giữ font mã" (yêu cầu #3) là **không đụng** chữ
 * mang mark `code`, và `applyFormatPlan` làm đúng thế.
 */
/** Font hệ thống an toàn — không tải gì (plan §3.2). Web và API dùng chung. */
export const CHILLDOC_SYSTEM_FONTS = [
    'Arial',
    'Times New Roman',
    'Georgia',
    'Verdana',
    'Courier New',
];
export const FORMAT_MAX_BLOCKS = 400;
export const FORMAT_TEXT_SAMPLE = 120;
export const FORMAT_SIZE_MIN = 10;
export const FORMAT_SIZE_MAX = 40;
const Size = z.number().int().min(FORMAT_SIZE_MIN).max(FORMAT_SIZE_MAX);
const FontName = z.string().trim().min(1).max(60);
/** Trần thụt lề (mm) — cùng `MAX_INDENT_MM` của `paragraph-indent.ts`. */
export const FORMAT_MAX_INDENT_MM = 150;
/** Kiểu ngắt dòng AI / bộ phân tích được chọn cho ảnh — tập con dễ đoán của bảy kiểu (APM-OBJ-a). */
export const IMAGE_WRAPS = ['inline', 'square', 'topBottom'];
export const IMAGE_ALIGNS = ['left', 'center', 'right'];
/** Ảnh hẹp / rộng nhất được đặt, % bề ngang vùng chữ. */
export const IMAGE_MIN_PCT = 10;
export const IMAGE_MAX_PCT = 100;
/** Số ảnh tối đa mô tả / đổi trong một khối. */
export const MAX_BLOCK_IMAGES = 10;
/**
 * Một ảnh trong khối: đủ để chọn cỡ và chỗ đặt mà không cần xem ảnh.
 * `width` = bề ngang đang hiện (px), `null` = cỡ gốc; `ratio` = rộng / cao.
 */
export const ImageSummary = z.object({
    width: z.number().int().positive().max(5000).nullable(),
    ratio: z.number().positive().max(100).nullable(),
    alt: z.string().max(80),
    wrap: z.string().max(16),
});
/** Cách đặt một ảnh — theo thứ tự ảnh trong khối. `widthPct` vắng = giữ cỡ đang có. */
export const ImageChange = z.object({
    widthPct: z.number().int().min(IMAGE_MIN_PCT).max(IMAGE_MAX_PCT).optional(),
    wrap: z.enum(IMAGE_WRAPS),
    align: z.enum(IMAGE_ALIGNS),
});
/** Thụt lề của một đoạn (mm): `first` âm = thụt treo, đúng quy ước `indentFirst`. */
export const FormatIndent = z.object({
    first: z.number().min(-FORMAT_MAX_INDENT_MM).max(FORMAT_MAX_INDENT_MM),
    left: z.number().min(0).max(FORMAT_MAX_INDENT_MM),
});
export const FORMULA_ALIGNS = ['left', 'center', 'right'];
/**
 * Cách đặt **mọi** ảnh / hình vẽ đứng riêng của tài liệu (APM-FMT-g2): kênh để
 * "căn giữa các ảnh" — yêu cầu thường gặp mà model từng không có chỗ nộp.
 */
export const FormatObjects = z.object({
    align: z.enum(IMAGE_ALIGNS).optional(),
    wrap: z.enum(['inline', 'topBottom']).optional(),
});
/** Một khối của tài liệu, rút gọn — **không** gửi cả tài liệu cho model. */
export const FormatBlockSummary = z.object({
    index: z.number().int().nonnegative(),
    type: z.enum([
        'paragraph',
        'heading',
        'list',
        'quote',
        'code',
        'table',
        'image',
        'formula',
        'other',
    ]),
    level: z.number().int().min(1).max(6).optional(),
    /** ≤ 120 ký tự đầu. */
    text: z.string().max(FORMAT_TEXT_SAMPLE),
    fonts: z.array(z.string().max(60)).max(10),
    sizes: z.array(z.number()).max(10),
    // --- Đợt hai (APM-FMT-a): tuỳ chọn, cho bộ phân tích local và skill v2 ---
    /** Khối `list`: loại và số mục. */
    list: z.enum(['bullet', 'ordered']).optional(),
    items: z.number().int().nonnegative().optional(),
    /** Thụt lề đang có của đoạn / tiêu đề (mm). */
    indentLeft: z.number().min(0).max(200).optional(),
    indentFirst: z.number().min(-200).max(200).optional(),
    /** Số gõ tay ở đầu dòng đã nhận dạng ("1.2.", "I.", "a)", "Chương 2"). */
    marker: z.string().max(24).optional(),
    images: z.array(ImageSummary).max(MAX_BLOCK_IMAGES).optional(),
    /** Khối `formula` (công thức đứng riêng): căn hiện tại. */
    formulaAlign: z.enum(FORMULA_ALIGNS).optional(),
    /** Số ký tự của khối và số ký tự mang mark `code` — tỉ trọng font tính trên phần còn lại. */
    chars: z.number().int().nonnegative().optional(),
    codeChars: z.number().int().nonnegative().optional(),
    // --- Đặc trưng cho dàn ý (APM-FMT-i): model thấy cấu trúc, không chỉ 120 ký tự chữ ---
    /** Số dòng ngắt mềm (`hardBreak`) trong khối. */
    lines: z.number().int().positive().optional(),
    /** Dòng VIẾT HOA ngắn. */
    upper: z.boolean().optional(),
    /** Khối `list`: chữ đầu của mục đầu và cấp tiêu đề của nó nếu mục là tiêu đề đánh số (APM-CD-i). */
    first: z.string().max(FORMAT_TEXT_SAMPLE).optional(),
    firstLevel: z.number().int().min(1).max(6).optional(),
    /** Số của dấu gõ tay ("3." ⇒ 3, "7.2" ⇒ 7, "IV." ⇒ 4) và số thành phần ("7.2" ⇒ 2). */
    markerNumber: z.number().int().nonnegative().optional(),
    markerParts: z.number().int().positive().optional(),
    /**
     * Khối `list`: từng mục theo thứ tự trải phẳng (≤ 20) — một phần có thể bắt đầu
     * BÊN TRONG danh sách (lượt áp trước gom "5. Thay thế cache…" vào cùng danh sách
     * với các dòng "-" của bài 4; chỉ đưa mục đầu là giấu mất bài 5).
     */
    entries: z
        .array(z.object({
        text: z.string().max(80),
        level: z.number().int().min(1).max(6).optional(),
        marker: z.string().max(24).optional(),
        number: z.number().int().nonnegative().optional(),
        depth: z.number().int().min(0).max(4).optional(),
    }))
        .max(20)
        .optional(),
});
/**
 * Đổi một khối. `index` là khối **cấp cao nhất**; `item` (tuỳ chọn) trỏ vào mục
 * thứ mấy của một khối `list` (theo thứ tự trải phẳng), không có = cả khối.
 *
 * - `heading` / `paragraph`: đổi loại, **giữ chữ** (kể cả số gõ tay).
 * - `numberedHeading`: tiêu đề đánh số tự động (APM-CD-i: `orderedList > listItem
 *   > heading`); số gõ tay ở đầu bị bỏ vì số tự động thay nó — ngoại lệ duy nhất
 *   của "không đụng chữ", cùng luật với dòng vừa thành mục danh sách khi dán.
 * - `keep`: giữ loại, chỉ áp `indent` / `images` / `formulaAlign`.
 */
export const FormatBlockChange = z.object({
    index: z.number().int().nonnegative(),
    item: z.number().int().nonnegative().optional(),
    /** `bullet` / `ordered` (APM-FMT-h): đoạn thành mục danh sách; các đoạn liền nhau gom thành một danh sách. */
    /** `headingSplit` (APM-FMT-h): câu đầu của đoạn thành tiêu đề, phần còn lại giữ làm đoạn — chữ không đổi, chỉ chia khối. */
    as: z.enum([
        'paragraph',
        'heading',
        'numberedHeading',
        'headingSplit',
        'insertHeading',
        'bullet',
        'ordered',
        'keep',
    ]),
    level: z.number().int().min(1).max(4).optional(),
    /**
     * `insertHeading` (APM-FMT-h): CHÈN một tiêu đề mới mang `text` ngay TRƯỚC khối `index`, khối ấy giữ nguyên —
     * cho yêu cầu kiểu "đặt tên Bài 1…Bài 8". Ngoại lệ có chủ đích của "không đụng chữ": người dùng yêu cầu thêm chữ.
     */
    text: z.string().trim().min(1).max(80).optional(),
    /** `bullet` / `ordered`: độ sâu 0–4. */
    depth: z.number().int().min(0).max(4).optional(),
    /** `bullet` / `ordered`: mục này mở một danh sách MỚI, không nối vào danh sách ngay phía trên (hai cụm khác mức thụt lề). */
    newList: z.boolean().optional(),
    /**
     * Với thay đổi trỏ vào `item` của một khối `list` (APM-FMT-i): TÁCH danh sách tại mục ấy —
     * mục thành khối cấp cao nhất theo `as` (tiêu đề / đoạn / chèn tiêu đề trước), các mục sau
     * thành danh sách mới. Không `lift` thì mục đổi loại tại chỗ (tiêu đề trong mục = tiêu đề đánh số).
     */
    lift: z.boolean().optional(),
    /** Ghi đè thụt lề cấp tài liệu cho riêng khối này. */
    indent: FormatIndent.optional(),
    /** Khối có ảnh: cách đặt từng ảnh, theo thứ tự. */
    images: z.array(ImageChange).min(1).max(MAX_BLOCK_IMAGES).optional(),
    /** Khối `formula`: căn của công thức đứng riêng. */
    formulaAlign: z.enum(FORMULA_ALIGNS).optional(),
});
/**
 * Kế hoạch định dạng. `null` ở font/cỡ = "về mặc định" (bỏ font/cỡ riêng lẻ).
 * `blocks`: khối nào nên đổi thành đoạn thường hay tiêu đề cấp mấy.
 *
 * `indent` (đợt hai): thụt lề theo **lớp** — `paragraph` cho đoạn ngoài danh
 * sách, `list` cho đoạn trong mục; tiêu đề luôn về 0. `null`/vắng = không đụng.
 */
export const FormatPlan = z.object({
    bodyFont: FontName.nullable(),
    bodySize: Size.nullable(),
    headingFont: FontName.nullable(),
    headingSizes: z.object({
        h1: Size.nullable(),
        h2: Size.nullable(),
        h3: Size.nullable(),
        h4: Size.nullable(),
    }),
    indent: z.object({ paragraph: FormatIndent, list: FormatIndent }).nullable().optional(),
    /** Ảnh / hình vẽ đứng riêng của cả tài liệu — áp **sau** thay đổi theo khối (ý người dùng thắng). */
    objects: FormatObjects.nullable().optional(),
    blocks: z.array(FormatBlockChange).max(FORMAT_MAX_BLOCKS),
    /** ≤ 3 câu tiếng Việt: đã đổi gì, vì sao. */
    notes: z.array(z.string().trim().max(200)).max(3),
});
/** Đường làm tay — "Chuẩn hoá định dạng": bỏ mọi font/cỡ lẻ, về mặc định của app. */
export const DEFAULT_FORMAT_PLAN = {
    bodyFont: null,
    bodySize: null,
    headingFont: null,
    headingSizes: { h1: null, h2: null, h3: null, h4: null },
    blocks: [],
    notes: [],
};
// ---------------------------------------------------------------------------
// Câu hỏi cho điểm mập mờ — APM-FMT-b/c: bộ phân tích local quyết mọi thứ đo
// được; chỗ phải "đoán gu" thành một câu hỏi có lựa chọn. Model (hoặc người
// dùng) chỉ **chọn**, `mergeAnswers` ghép vào kế hoạch nháp bằng hàm thuần.
// ---------------------------------------------------------------------------
export const FORMAT_MAX_QUESTIONS = 60;
export const FORMAT_QUESTION_KINDS = [
    /** Dòng ngắn không đánh số là gì? options `h2`…`h4` | `paragraph` | `bullet` (mục đầu của danh sách ngay dưới). */
    'heading',
    /** Hai font ngang nhau — cái nào là chữ thân? options = tên font. */
    'fontRole',
    /** Lớp không có đa số thụt lề. options `first:left` (mm), `scope` = lớp. */
    'indent',
    /** Ảnh cỡ vừa giữa chữ. options `wrap:widthPct:align`, `slot` = ảnh thứ mấy. */
    'image',
    /** (Dán) Cụm tiêu đề dán ngang hàng hay là con của mục đang chứa. options `sibling` | `child`. */
    'level',
    /** (Dán) Dời ảnh lên ngay sau câu dẫn? options `move` | `stay`; `target` = chỉ số câu dẫn. */
    'imageMove',
    /**
     * (Nhập PDF — APM-CDIMP) Bước thụt lề của mục không rõ: mục này sâu mấy? options `0`…`4`.
     * Chỉ `applyImportAnswers` (`chilldoc-import.ts`) đọc; `mergeAnswers` bỏ qua.
     */
    'listDepth',
    /**
     * (Nhập PDF — APM-CDIMP-j) Gạch nối cuối dòng không phân biệt được từ ghép thật (`top-level`) với từ bị
     * ngắt dòng (`sys-` + `tem`). options = hai cách viết `[bỏ gạch, giữ gạch]`, default = bỏ gạch (bản thường);
     * `index` khối, `offset` vị trí ký tự của từ trong chữ của khối **sau** khi đã nối theo mặc định.
     * Chỉ `applyImportAnswers` đọc; `mergeAnswers` bỏ qua.
     */
    'hyphen',
];
export const FormatQuestion = z.object({
    id: z.string().trim().min(1).max(40),
    kind: z.enum(FORMAT_QUESTION_KINDS),
    /** Khối cấp cao nhất (và mục) mà câu hỏi trỏ tới. */
    index: z.number().int().nonnegative().optional(),
    item: z.number().int().nonnegative().optional(),
    /** `image`: ảnh thứ mấy trong khối. */
    slot: z.number().int().nonnegative().optional(),
    /** `indent`: lớp. */
    scope: z.enum(['paragraph', 'list']).optional(),
    /** `imageMove`: khối câu dẫn (chỉ số trong đoạn dán, -1 = đầu). */
    target: z.number().int().min(-1).optional(),
    /** `imageMove`: các khối ảnh đi cùng nhau (theo thứ tự), dời thành một hàng sau `target`. */
    group: z.array(z.number().int().nonnegative()).max(10).optional(),
    /** `hyphen`: vị trí ký tự của từ (theo chữ của khối, mọi run nối lại). */
    offset: z.number().int().nonnegative().optional(),
    /** Chữ của khối (≤ 120 ký tự) — để model / người dùng hiểu đang hỏi gì. */
    text: z.string().max(FORMAT_TEXT_SAMPLE).optional(),
    options: z.array(z.string().trim().min(1).max(40)).min(2).max(4),
    default: z.string().trim().min(1).max(40),
});
/**
 * Dàn ý toàn tài liệu do model trả (APM-FMT-i, "outline-first"): phần nào bắt
 * đầu ở khối nào, cấp mấy, có chèn tiêu đề mới không; danh sách từ khối nào
 * tới khối nào. Code dựng kế hoạch từ đây (`planFromOutline`) — model quyết cấu
 * trúc toàn cục, code quyết cách áp và kiểm luật.
 */
export const OutlineSection = z.object({
    index: z.number().int().nonnegative(),
    /** Khối `list`: phần bắt đầu ở mục thứ `item` (theo `entries`); danh sách được tách tại đó. */
    item: z.number().int().nonnegative().optional(),
    level: z.number().int().min(1).max(4),
    /** Chèn tiêu đề MỚI mang chữ này ngay trước khối; khối giữ nguyên. Vắng ⇒ chính khối là tiêu đề. */
    title: z.string().trim().min(1).max(80).optional(),
});
export const OutlineList = z.object({
    from: z.number().int().nonnegative(),
    to: z.number().int().nonnegative(),
    kind: z.enum(['bullet', 'ordered']),
    depth: z.number().int().min(0).max(4).optional(),
});
export const FormatOutline = z.object({
    sections: z.array(OutlineSection).max(FORMAT_MAX_BLOCKS),
    lists: z.array(OutlineList).max(200),
});
/** Khối đứng riêng ngắn thành tiêu đề nguyên dòng; dài hơn thì tách câu đầu. */
const OUTLINE_WHOLE_HEADING_CHARS = 160;
/**
 * Dàn ý ⇒ thay đổi theo khối. Thay đổi **cấu trúc** của nháp (tiêu đề, danh sách)
 * bị dàn ý thay thế; thay đổi khác của nháp (ảnh, công thức, thụt lề riêng) giữ.
 * Hàm thuần — web (xem trước) và API (`finalize`) dùng chung.
 */
export function planFromOutline(draft, outline, blocks) {
    const byIndex = new Map(blocks.map((block) => [block.index, block]));
    const structural = new Set([
        'heading',
        'numberedHeading',
        'headingSplit',
        'insertHeading',
        'bullet',
        'ordered',
        'paragraph',
    ]);
    let changes = draft.blocks.filter((change) => !structural.has(change.as));
    const add = (change) => {
        changes = upsertBlock(changes, change);
    };
    for (const section of outline.sections) {
        const block = byIndex.get(section.index);
        if (!block)
            continue;
        if (block.type === 'list' && section.item !== undefined) {
            // Phần bắt đầu bên trong danh sách: tách danh sách tại mục ấy, mục thành tiêu đề cấp cao nhất.
            if (section.title) {
                add({
                    index: section.index,
                    item: section.item,
                    as: 'insertHeading',
                    level: section.level,
                    text: section.title,
                    lift: true,
                });
            }
            const entry = block.entries?.[section.item];
            const chars = entry?.text.length ?? 0;
            add({
                index: section.index,
                item: section.item,
                as: chars >= 80 ? 'headingSplit' : 'heading',
                level: section.level,
                lift: true,
            });
            continue;
        }
        if (section.title) {
            add({ index: section.index, as: 'insertHeading', level: section.level, text: section.title });
            continue;
        }
        if (block.type === 'list') {
            // Mục đầu của khối danh sách là tiêu đề (tiêu đề đánh số tự động, APM-CD-i).
            add({ index: section.index, item: 0, as: 'heading', level: section.level });
        }
        else if (block.type === 'paragraph' || block.type === 'heading') {
            const chars = block.chars ?? block.text.length;
            add({
                index: section.index,
                as: chars > OUTLINE_WHOLE_HEADING_CHARS ? 'headingSplit' : 'heading',
                level: section.level,
            });
        }
    }
    const ranges = [...outline.lists].sort((a, b) => a.from - b.from);
    const covered = new Map();
    for (const range of ranges) {
        for (let index = range.from; index <= range.to; index += 1)
            covered.set(index, range);
    }
    for (const range of ranges) {
        for (let index = range.from; index <= range.to; index += 1) {
            const block = byIndex.get(index);
            if (!block || block.type !== 'paragraph')
                continue;
            const previous = covered.get(index - 1);
            const newList = index === range.from && previous !== undefined && previous !== range;
            add({
                index,
                as: range.kind,
                depth: range.depth ?? 0,
                ...(newList ? { newList: true } : {}),
            });
        }
    }
    return { ...draft, blocks: changes };
}
/** Câu trả lời của model / người dùng: chỉ `choice` trong `options` của câu hỏi cùng `id`. */
export const FormatAnswer = z.object({
    id: z.string().trim().min(1).max(40),
    choice: z.string().trim().min(1).max(40),
    /** Một câu vì sao — hiện cạnh lựa chọn. */
    why: z.string().trim().max(120).optional(),
});
/** Đầu ra của skill v2: câu trả lời + (chỉ khi có yêu cầu thêm) ghi đè cấp tài liệu. */
export const FormatAnswers = z.object({
    answers: z.array(FormatAnswer).max(FORMAT_MAX_QUESTIONS),
    overrides: FormatPlan.pick({
        bodyFont: true,
        bodySize: true,
        headingFont: true,
        headingSizes: true,
        indent: true,
        objects: true,
    })
        .partial()
        .extend({
        /** Thay đổi theo khối model đề xuất thêm so với nháp (APM-FMT-h) — chỉ khối cần đổi. */
        blocks: z.array(FormatBlockChange).max(FORMAT_MAX_BLOCKS).optional(),
    })
        .optional(),
    /** Dàn ý toàn tài liệu (APM-FMT-i) — có mặt thì thay cấu trúc của nháp. */
    outline: FormatOutline.optional(),
    notes: z.array(z.string().trim().max(200)).max(3),
});
/** Cùng khối — và cùng loại thao tác: `insertHeading` (chèn trước khối) sống cạnh thay đổi của chính khối ấy. */
const sameBlock = (a, b) => a.index === b.index &&
    (a.item ?? -1) === (b.item ?? -1) &&
    (a.as === 'insertHeading') === (b.as === 'insertHeading');
/**
 * Thay đổi mới **gộp** lên thay đổi cũ của cùng khối: trường model / người dùng
 * đưa ra thắng, trường nháp không bị nhắc tới (`newList`, `images`…) giữ nguyên.
 * Lượt thật 2026-09-29: model đề xuất lại `{ index: 18, as: 'bullet' }` làm mất
 * `newList: true` của nháp ⇒ hai danh sách dính vào nhau.
 */
function upsertBlock(blocks, change) {
    const existing = blocks.find((block) => sameBlock(block, change));
    const rest = blocks.filter((block) => !sameBlock(block, change));
    const merged = existing ? { ...existing, ...change } : change;
    return [...rest, merged].sort((a, b) => a.index - b.index || (a.item ?? -1) - (b.item ?? -1));
}
const numbers = (value) => value.split(':').map((part) => Number.parseFloat(part));
/**
 * Ghép câu trả lời vào kế hoạch nháp — hàm thuần, dùng chung web (xem trước,
 * người dùng tự chọn) và API (`finalize`). Câu hỏi không có câu trả lời ⇒ giữ
 * nháp (nháp đã mang `default`). Trả lời ngoài `options` ⇒ bỏ qua. Trả lời
 * bằng `default` vẫn được ghép — **idempotent**, nên đổi tay về mặc định sau
 * khi AI đã chọn khác vẫn có tác dụng (APM-FMT-d).
 *
 * `level` / `imageMove` là của đoạn dán và đổi `PastePlan`, không phải
 * `FormatPlan` — xem `mergePasteAnswers` ở `chilldoc-paste.ts`.
 */
export function mergeAnswers(draft, questions, answers) {
    const byId = new Map(answers.map((answer) => [answer.id, answer.choice]));
    let plan = { ...draft, blocks: [...draft.blocks] };
    for (const question of questions) {
        const choice = byId.get(question.id);
        if (choice === undefined || !question.options.includes(choice))
            continue;
        switch (question.kind) {
            case 'heading': {
                if (question.index === undefined)
                    break;
                const at = {
                    index: question.index,
                    ...(question.item !== undefined ? { item: question.item } : {}),
                };
                if (choice === 'paragraph') {
                    plan = { ...plan, blocks: plan.blocks.filter((block) => !sameBlock(block, at)) };
                }
                else if (choice === 'bullet' || choice === 'ordered') {
                    // Dòng không dấu kẹp giữa dòng dẫn ":" và chuỗi gạch đầu dòng ⇒ mục đầu của danh sách ấy.
                    plan = { ...plan, blocks: upsertBlock(plan.blocks, { ...at, as: choice, depth: 0 }) };
                }
                else {
                    const level = Number(choice.replace(/^h/, ''));
                    if (level >= 1 && level <= 4) {
                        plan = { ...plan, blocks: upsertBlock(plan.blocks, { ...at, as: 'heading', level }) };
                    }
                }
                break;
            }
            case 'fontRole':
                plan = {
                    ...plan,
                    bodyFont: choice,
                    headingFont: plan.headingFont === choice ? null : plan.headingFont,
                };
                break;
            case 'indent': {
                const [first, left] = numbers(choice);
                if (!question.scope || first === undefined || left === undefined)
                    break;
                const current = plan.indent ?? {
                    paragraph: { first: 0, left: 0 },
                    list: { first: 0, left: 0 },
                };
                plan = { ...plan, indent: { ...current, [question.scope]: { first, left } } };
                break;
            }
            case 'image': {
                if (question.index === undefined)
                    break;
                const [wrap, pct, align] = choice.split(':');
                const widthPct = Number.parseInt(pct ?? '', 10);
                if (!wrap || !align)
                    break;
                const existing = plan.blocks.find((block) => block.index === question.index && block.item === undefined);
                const images = [...(existing?.images ?? [])];
                const slot = question.slot ?? 0;
                while (images.length <= slot)
                    images.push({ wrap: 'inline', align: 'left' });
                images[slot] = {
                    wrap: wrap,
                    align: align,
                    ...(Number.isFinite(widthPct) ? { widthPct } : {}),
                };
                plan = {
                    ...plan,
                    blocks: upsertBlock(plan.blocks, {
                        ...(existing ?? { index: question.index, as: 'keep' }),
                        images,
                    }),
                };
                break;
            }
            default:
                break;
        }
    }
    return plan;
}
/**
 * Ghi đè cấp tài liệu của model đè lên kế hoạch. `null` / vắng = **giữ nháp**
 * (APM-FMT-g2): lượt thật 2026-09-29 model nộp toàn `null` với nghĩa "không
 * đổi", mà `null` trong kế hoạch là "về mặc định" ⇒ font thân bị xoá sạch. Muốn
 * về mặc định của ứng dụng thì người dùng chọn ở hộp thoại, không qua model.
 */
export function withOverrides(plan, overrides) {
    if (!overrides)
        return plan;
    const next = { ...plan };
    if (overrides.bodyFont != null)
        next.bodyFont = overrides.bodyFont;
    if (overrides.bodySize != null)
        next.bodySize = overrides.bodySize;
    if (overrides.headingFont != null)
        next.headingFont = overrides.headingFont;
    if (overrides.headingSizes != null) {
        next.headingSizes = { ...plan.headingSizes };
        for (const key of ['h1', 'h2', 'h3', 'h4']) {
            const size = overrides.headingSizes[key];
            if (size != null)
                next.headingSizes[key] = size;
        }
    }
    if (overrides.indent != null)
        next.indent = overrides.indent;
    if (overrides.objects != null)
        next.objects = { ...plan.objects, ...overrides.objects };
    if (overrides.blocks?.length) {
        let blocks = next.blocks;
        for (const change of overrides.blocks)
            blocks = upsertBlock(blocks, change);
        next.blocks = blocks;
    }
    return next;
}
/** Cấu hình tiêu đề / đánh số của tài liệu, rút gọn cho model (APM-FMT-c). */
export const FormatSettings = z.object({
    baseSize: z.number().min(6).max(200).optional(),
    // `partialRecord`: zod 4 coi `record(enum)` là bắt buộc đủ mọi khoá.
    headings: z
        .partialRecord(z.enum(['h1', 'h2', 'h3', 'h4']), z.object({
        size: z.number().min(6).max(200).optional(),
        bold: z.boolean().optional(),
        font: z.string().trim().min(1).max(60).optional(),
    }))
        .optional(),
    /** Id preset đánh số (`NUMBERING_PRESETS`) hay mẫu cấp 1–3 nối bằng " › ". */
    numbering: z.string().trim().max(80).optional(),
});
/**
 * Đầu vào của `POST /me/files/format-plan` — từ APM-FMT-c client gửi **kế
 * hoạch nháp** đã tính local cùng danh sách câu hỏi; model chỉ trả lời câu hỏi.
 * Không câu hỏi và không yêu cầu thêm ⇒ server trả nháp, không gọi LLM.
 */
export const FormatDocumentInput = z.object({
    blocks: z.array(FormatBlockSummary).max(FORMAT_MAX_BLOCKS),
    /** Font Google tài liệu đã thêm — kế hoạch chỉ được chọn trong đây + font hệ thống. */
    fonts: z.array(FontName).max(30),
    /**
     * Yêu cầu thêm của người dùng ("trang trọng hơn"…). TẠM THỜI không giới hạn
     * độ dài (2026-09-29, người dùng muốn thử dán kiến thức dài) — trần thực tế là
     * body JSON 1 MB. Trước đó là `.max(500)`; khôi phục khi thử xong.
     */
    prompt: z.string().trim().optional(),
    runId: z.uuid().optional(),
    draft: FormatPlan,
    questions: z.array(FormatQuestion).max(FORMAT_MAX_QUESTIONS),
    settings: FormatSettings.optional(),
    /**
     * Chế độ "Căn chỉnh bằng AI" (APM-FMT-g): người dùng bấm là muốn model chạy,
     * kể cả khi không có câu hỏi — model trả kế hoạch cấp tài liệu (`overrides`).
     */
    runAlways: z.boolean().optional(),
    /** Bật agent phản biện (APM-FMT-h): lượt model thứ hai đọc kế hoạch và bắt lỗi trước khi trả về. */
    critic: z.boolean().optional(),
});
export const FormatDocumentResult = z.object({
    plan: FormatPlan,
    /** Chuỗi thập phân như mọi số tiền AI khác — không làm tròn qua `number`. */
    costUsd: UsdAmount,
    partial: z.boolean(),
    /** Câu trả lời đã lọc (APM-FMT-c) — giao diện hiện "AI quyết gì, vì sao". Rỗng khi không có lượt LLM. */
    answers: z.array(FormatAnswer).max(FORMAT_MAX_QUESTIONS).default([]),
});
//# sourceMappingURL=chilldoc-format.js.map